/* eslint-disable @typescript-eslint/no-shadow */
import {
  DndProvider,
  DropOptions,
  getBackendOptions,
  getDescendants,
  MultiBackend,
  Tree,
  NodeModel,
} from "@minoru/react-dnd-treeview";
import React from "react";
import Node from "./TreeNode";
import Placeholder from "./Placeholder";
import useTreeOpenHandler from "./useTreeOpenHandler";
import styles from "./styles.module.css";
import { GQLContentForm } from "@quales/data-access/graphql";
import { GetFormSchemaPath, GetUISchemaPath } from "./utils";

const reorderArray = (
  array: NodeModel[],
  sourceIndex: number,
  targetIndex: number
) => {
  const newArray = [...array];
  const element = newArray.splice(sourceIndex, 1)[0];
  newArray.splice(targetIndex, 0, element);
  return newArray;
};

interface FormViewerTreeComponentProps {
  onTreeNodeClicked: (node: any) => void;
  inputFormData: GQLContentForm;
  onNewItem: (node: any, type: string) => void;
  onContentCopyClicked: (node: NodeModel) => void;
  onNewUiOrderObject: (uiOrder: string[], parentID: string) => void;
}

const FormViewerTreeComponent = ({
  onTreeNodeClicked,
  inputFormData,
  onNewItem,
  onContentCopyClicked,
  onNewUiOrderObject,
}: FormViewerTreeComponentProps) => {
  const { ref, getPipeHeight, toggle, open } = useTreeOpenHandler();

  const [treeData, setTreeData] = React.useState<NodeModel[]>([]);
  const [selectedTreeNode, setSelectedTreeNode] = React.useState<NodeModel>();
  const handleDrop = (newTree: NodeModel[], e: DropOptions) => {
    const { dragSourceId, dropTargetId, destinationIndex } = e;
    if (
      typeof dragSourceId === "undefined" ||
      typeof dropTargetId === "undefined"
    )
      return;
    const start = treeData.find((v) => v.id === dragSourceId);
    const end = treeData.find((v) => v.id === dropTargetId);

    if (
      start?.parent === dropTargetId &&
      start &&
      typeof destinationIndex === "number"
    ) {
      setTreeData((treeData) => {
        const output = reorderArray(
          treeData,
          treeData.indexOf(start),
          destinationIndex
        );
        const findParentItems = output.filter(
          (item) => item.parent === start.parent
        );

        const uiOrderObject = findParentItems.map((item) => item.id);
        if (start.parent) {
          //@ts-ignore
          onNewUiOrderObject(uiOrderObject, start.parent);
        }

        return output;
      });
    }

    if (
      start?.parent !== dropTargetId &&
      start &&
      typeof destinationIndex === "number"
    ) {
      if (
        getDescendants(treeData, dragSourceId).find(
          (el) => el.id === dropTargetId
        ) ||
        dropTargetId === dragSourceId ||
        (end && !end?.droppable)
      )
        return;
      //@ts-ignore
      setTreeData((treeData) => {
        const output = reorderArray(
          treeData,
          treeData.indexOf(start),
          destinationIndex
        );
        const movedElement = output.find((el) => el.id === dragSourceId);
        if (movedElement) movedElement.parent = dropTargetId;
        return output;
      });
    }
  };

  React.useEffect(() => {
    if (
      inputFormData === undefined ||
      (inputFormData && !inputFormData.formSchema) ||
      (inputFormData &&
        inputFormData.formSchema &&
        Object.keys(inputFormData.formSchema).length === 0) ||
      Object.keys(inputFormData.formSchema.properties).length === 0
    ) {
      setTreeData([
        {
          id: "root",
          parent: "base",
          droppable: true,
          //@ts-ignore
          type: "object",
          text: "Root",
        },
      ]);
    } else {
      if (
        Object.hasOwn(inputFormData.uiSchema, "ui:order") &&
        Object.hasOwn(inputFormData.uiSchema, "ui:order")
      ) {
        const subArray = inputFormData.formSchema.properties;
        const arrayFromObject = [];

        Object.entries(subArray).forEach(([key, value]) => {
          //@ts-ignore
          if (value.type === "object" && key !== "root") {
            Object.entries(subArray[key].properties).forEach(
              ([childKey, childValue]) => {
                //@ts-ignore
                if (childValue.type === "object" && key !== "root") {
                  Object.entries(
                    subArray[key].properties[childKey].properties
                  ).forEach(([subChildKey, subChildValue]) => {
                    arrayFromObject.push({
                      id: subChildKey,
                      parent: childKey,
                      isOpen: true,
                      //@ts-ignore
                      droppable: subChildValue.type === "object" ? true : false,
                      //@ts-ignore
                      type: subChildValue.type,
                      //@ts-ignore
                      text: subChildValue.title,
                    });
                  });
                }

                arrayFromObject.push({
                  id: childKey,
                  parent: key,
                  isOpen: true,

                  //@ts-ignore
                  droppable: childValue.type === "object" ? true : false,
                  //@ts-ignore
                  type: childValue.type,
                  //@ts-ignore
                  text: childValue.title,
                });
              }
            );
          }

          arrayFromObject.push({
            id: key,
            parent: "root",
            isOpen: true,
            //@ts-ignore
            droppable: value.type === "object" ? true : false,
            //@ts-ignore
            type: value.type,
            //@ts-ignore
            text: value.title,
          });
        });

        let newResult = [];

        arrayFromObject.map((item) => {
          const leafArray = [];

          if (
            !newResult.some(
              (res) => res.parent.toString() === item.parent.toString()
            )
          ) {
            if (item.parent === "root") {
              const filteredRootItems = arrayFromObject.filter(
                (item) => item.parent === "root"
              );
              const uiSchemaArray = inputFormData.uiSchema["ui:order"];

              uiSchemaArray.forEach((uiOrderItem) => {
                const foundObj = filteredRootItems.find(
                  (filteredObj) => filteredObj.id === uiOrderItem
                );

                if (foundObj) {
                  leafArray.push(foundObj);
                }
              });
            } else {
              const getFormSchemaPath = GetFormSchemaPath(
                item.parent,
                inputFormData
              );
              const filteredRootItems = arrayFromObject.filter(
                (fill) => fill.parent === item.parent
              );

              const getUiSchemaPath = GetUISchemaPath(getFormSchemaPath);

              const uiSchemaArray =
                inputFormData.uiSchema[getUiSchemaPath]["ui:order"];

              uiSchemaArray.forEach((uiOrderItem) => {
                const foundObj = filteredRootItems.find(
                  (filteredObj) => filteredObj.id === uiOrderItem
                );

                if (foundObj) {
                  leafArray.push(foundObj);
                }
              });
            }

            newResult = [...newResult, ...leafArray];
          }
        });

        newResult.unshift({
          id: "root",
          parent: "base",
          isOpen: true,
          droppable: true,
          //@ts-ignore
          type: "object",
          text: "Root",
        });

        open("root");

        setTreeData(newResult);
      } else {
        const subArray = inputFormData.formSchema.properties;
        const arrayFromObject = [];
        Object.entries(subArray).forEach(([key, value]) => {
          //@ts-ignore
          if (value.type === "object" && key !== "root") {
            Object.entries(subArray[key].properties).forEach(
              ([childKey, childValue]) => {
                //@ts-ignore
                if (childValue.type === "object" && key !== "root") {
                  Object.entries(
                    subArray[key].properties[childKey].properties
                  ).forEach(([subChildKey, subChildValue]) => {
                    arrayFromObject.push({
                      id: subChildKey,
                      parent: childKey,
                      isOpen: true,
                      //@ts-ignore
                      droppable: subChildValue.type === "object" ? true : false,
                      //@ts-ignore
                      type: subChildValue.type,
                      text: subChildKey,
                    });
                  });
                }

                arrayFromObject.push({
                  id: childKey,
                  parent: key,
                  isOpen: true,
                  //@ts-ignore
                  droppable: childValue.type === "object" ? true : false,
                  //@ts-ignore
                  type: childValue.type,
                  text: childKey,
                });
              }
            );
          }

          arrayFromObject.push({
            id: key,
            parent: "root",
            isOpen: true,
            //@ts-ignore
            droppable: value.type === "object" ? true : false,
            //@ts-ignore
            type: value.type,
            text: key,
          });
        });
        const combinedArray = [
          {
            id: "root",
            parent: "base",
            isOpen: true,
            droppable: true,
            //@ts-ignore
            type: "object",
            text: "Root",
          },
          ...arrayFromObject,
        ];

        setTreeData(combinedArray);
        open("root");
      }
    }
  }, [inputFormData]);

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <div className={styles.wrapper}>
        <Tree
          ref={ref}
          classes={{
            root: styles.treeRoot,
            placeholder: styles.placeholder,
            dropTarget: styles.dropTarget,
            listItem: styles.listItem,
          }}
          tree={treeData}
          sort={false}
          rootId={"base"}
          insertDroppableFirst={false}
          enableAnimateExpand={true}
          onDrop={handleDrop}
          canDrop={() => true}
          dropTargetOffset={5}
          placeholderRender={(node, { depth }) => (
            <Placeholder node={node} depth={depth} />
          )}
          render={(node, { depth, isOpen, isDropTarget }) => (
            <Node
              getPipeHeight={getPipeHeight}
              node={node}
              depth={depth}
              isSelected={
                selectedTreeNode && node.id === selectedTreeNode.id.toString()
              }
              isOpen={isOpen}
              onContentCopyClicked={(node: NodeModel) => {
                onContentCopyClicked(node);
              }}
              onClick={() => {
                if (node.droppable) {
                  toggle(node?.id);
                }
              }}
              onAddButtonClicked={(nodeID: any, type: string) => {
                onNewItem(node, type);
              }}
              onTreeItemClick={() => {
                setSelectedTreeNode(node);
                onTreeNodeClicked(node);
              }}
              isDropTarget={isDropTarget}
              treeData={treeData}
            />
          )}
          initialOpen={true}
        />
      </div>
    </DndProvider>
  );
};

export default FormViewerTreeComponent;
